import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Construction } from 'lucide-react';
import { ReviewHeader } from './ReviewHeader';
import { VenueDetailsModal } from '../Step1Research/VenueDetailsModal';
import { CventVenueMatchingResults } from './CventVenueMatchingResults';
import { LoadingState } from '@/components/ui/LoadingState';
import { Card, CardContent } from '@/components/ui/Card/Card';
import { VStack } from '@/components/ui/Stack';
import { useNotificationStore } from '@/stores/notificationStore';
import { ResearchService, type VenueDetails, type ResearchDestination, type ShortlistWithVenue } from '@/services/ResearchService';
import { EventsService, type EventDTO } from '@/services/EventsService';
import { CventIntegrationService, type VenueMatchResults } from '@/services/CventIntegrationService';
import { RFPVenuesTable } from './RFPVenuesTable';

interface RFPReviewStepProps {
  eventId: string;
  onBack: () => void;
  onContinueToSelection?: () => void;
  submittedProposalCount?: number;
}

export function RFPReviewStep({ eventId, onBack, onContinueToSelection, submittedProposalCount = 0 }: RFPReviewStepProps) {
  const [event, setEvent] = useState<EventDTO | null>(null);
  const [destinations, setDestinations] = useState<Array<{
    destination: ResearchDestination;
    shortlist: ShortlistWithVenue[];
  }>>([]);
  const [totalVenues, setTotalVenues] = useState(0);
  const [loading, setLoading] = useState(true);
  const [venueMatches, setVenueMatches] = useState<VenueMatchResults | null>(null);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<VenueDetails | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  
  const researchService = new ResearchService();
  const eventsService = new EventsService();
  const cventService = new CventIntegrationService();
  const notificationStore = useNotificationStore();
  const queryClient = useQueryClient();

  useEffect(() => {
    loadData();
  }, [eventId]);

  async function loadData() {
    try {
      setLoading(true);
      
      // Load event details (includes Cvent connection info)
      const eventData = await eventsService.get(eventId);
      setEvent(eventData);
      
      // Load shortlisted venues
      const venueData = await researchService.getAllShortlistedVenuesForEvent(eventId);
      setDestinations(venueData.destinations);
      setTotalVenues(venueData.total_venues);

      // If connected to Cvent RFP, load venue matches
      if (eventData.cvent_rfp_id) {
        await loadVenueMatches();
      } else {
        setVenueMatches(null);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      notificationStore.error('Failed to load event data', {
        title: 'Load Error',
      });
    } finally {
      setLoading(false);
    }
  }

  async function loadShortlistOnly() {
    try {
      // Only reload shortlisted venues without full page refresh
      const venueData = await researchService.getAllShortlistedVenuesForEvent(eventId);
      setDestinations(venueData.destinations);
      setTotalVenues(venueData.total_venues);
      
      // Invalidate the shortlist query used by ResearchProposalTool to update accessible steps
      queryClient.invalidateQueries({ queryKey: ['research-proposal-shortlist', eventId] });
    } catch (error) {
      console.error('Failed to load shortlist:', error);
      notificationStore.error('Failed to refresh shortlist', {
        title: 'Load Error',
      });
    }
  }

  async function loadVenueMatches() {
    try {
      setLoadingMatches(true);
      const matches = await cventService.getVenueMatches(eventId);
      setVenueMatches(matches.matchResults);
      
      // Reload event data to get updated sync timestamp after sync completes
      const updatedEventData = await eventsService.get(eventId);
      setEvent(updatedEventData);
    } catch (error) {
      console.error('Failed to load venue matches:', error);
      notificationStore.error('Failed to load venue matches', {
        title: 'Load Error',
      });
      setVenueMatches(null);
    } finally {
      setLoadingMatches(false);
    }
  }

  function handleViewDetails(venue: VenueDetails) {
    setSelectedVenue(venue);
    setDetailsModalOpen(true);
  }

  // Saves the hotel note shared with the RFP and Selection steps.
  async function handleUpdateNotes(shortlistId: string, notes: string, clientFacing: boolean) {
    try {
      await researchService.updateShortlistItem(shortlistId, { notes, notes_client_facing: clientFacing });
      
      // Update local state
      setDestinations(prev =>
        prev.map(dest => ({
          ...dest,
          shortlist: dest.shortlist.map(item =>
            item.id === shortlistId ? { ...item, notes, notes_client_facing: clientFacing } : item
          )
        }))
      );
      
      notificationStore.success('Notes saved', {
        title: 'Success',
      });
    } catch (error) {
      console.error('Failed to update notes:', error);
      notificationStore.error('Failed to save notes', {
        title: 'Error',
      });
      throw error; // Re-throw so NoteIcon can handle it
    }
  }

  // Flatten all venues from all destinations with destination info
  // Must be called before any conditional returns (Rules of Hooks)
  const venuesWithDestination = useMemo(() => {
    return destinations.flatMap((item) =>
      item.shortlist.map((shortlistItem) => ({
        venue: shortlistItem.venue,
        destination: item.destination,
        shortlistItem,
      }))
    );
  }, [destinations]);

  // Create a map of cvent_supplier_id -> venue name from match results
  const cventVenueMap = useMemo(() => {
    if (!venueMatches) return undefined;
    
    const map = new Map<string, string>();
    
    // Add matched venues
    venueMatches.matched.forEach(match => {
      if (match.cventVenue.id && match.cventVenue.venueName) {
        map.set(match.cventVenue.id, match.cventVenue.venueName);
      }
    });
    
    // Add in DB not shortlisted venues
    venueMatches.inDbNotShortlisted.forEach(item => {
      if (item.cventVenue.id && item.cventVenue.venueName) {
        map.set(item.cventVenue.id, item.cventVenue.venueName);
      }
    });
    
    // Add not in DB venues
    venueMatches.notInDb.forEach(venue => {
      if (venue.id && venue.venueName) {
        map.set(venue.id, venue.venueName);
      }
    });

    return map;
  }, [venueMatches]);

  // Flat list of every Cvent supplier on this RFP — passed to the venues
  // table so the manual-connect modal can surface them. We deduplicate by
  // supplier id (a venue can appear in multiple sub-lists).
  const cventSuppliersList = useMemo(() => {
    if (!venueMatches) return undefined;
    const byId = new Map<string, import('@/services/CventIntegrationService').CventVenueSummary>();
    venueMatches.matched.forEach(m => {
      if (m.cventVenue.id) byId.set(m.cventVenue.id, m.cventVenue);
    });
    venueMatches.inDbNotShortlisted.forEach(m => {
      if (m.cventVenue.id) byId.set(m.cventVenue.id, m.cventVenue);
    });
    venueMatches.notInDb.forEach(v => {
      if (v.id) byId.set(v.id, v);
    });
    return Array.from(byId.values());
  }, [venueMatches]);

  if (loading) {
    return <LoadingState />;
  }

  // Empty state - no venues shortlisted. Only show when Cvent is NOT connected.
  // When Cvent IS connected, show main layout so Pending Connections (all Cvent venues) are visible.
  const hasShortlistedVenues = destinations.length > 0 && totalVenues > 0;
  const hasCventConnected = !!event?.cvent_rfp_id;
  if (!hasShortlistedVenues && !hasCventConnected) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="max-w-md w-full" padding="lg">
          <CardContent>
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full">
                <Construction className="w-8 h-8 text-gray-400" />
              </div>
              
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  No Venues Shortlisted
                </h2>
                <p className="text-muted-foreground">
                  Add venues to your shortlist in the Research step before continuing to RFPs.
                </p>
              </div>
              
              <div className="pt-2">
                <button
                  onClick={onBack}
                  className="text-primary-600 hover:text-primary-700 font-medium text-sm"
                >
                  ← Back to Research
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Prepare connected RFP info
  const connectedRfp = event?.cvent_rfp_id ? {
    id: event.cvent_rfp_id,
    name: event.cvent_rfp_name || '',
    code: event.cvent_rfp_code || '',
    connectedAt: event.cvent_connected_at || '',
    lastSyncedAt: event.cvent_data_last_synced_at || null,
    supplierCount: venueMatches ? 
      venueMatches.matched.length + venueMatches.inDbNotShortlisted.length + venueMatches.notInDb.length 
      : undefined,
  } : null;

  async function handleRefreshData() {
    try {
      setLoadingMatches(true);
      // Call refresh endpoint to force fresh data fetch
      const refreshedMatches = await cventService.refreshCventData(eventId);
      setVenueMatches(refreshedMatches.matchResults);
      
      // Reload event to get updated sync timestamp
      const updatedEvent = await eventsService.get(eventId);
      setEvent(updatedEvent);
    } catch (error) {
      console.error('Failed to refresh Cvent data:', error);
      notificationStore.error('Failed to refresh Cvent data', {
        title: 'Refresh Error',
      });
      throw error; // Re-throw so ReviewHeader can handle it
    } finally {
      setLoadingMatches(false);
    }
  }

  // Main review page
  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <ReviewHeader
        eventId={eventId}
        totalVenues={totalVenues}
        destinationCount={destinations.length}
        connectedRfp={connectedRfp}
        onBack={onBack}
        onRfpConnected={(matchResults) => {
          if (matchResults) {
            setVenueMatches(matchResults);
          }
          loadData();
        }}
        onRfpDisconnected={loadData}
        onRefresh={handleRefreshData}
        onContinueToSelection={onContinueToSelection}
        submittedProposalCount={submittedProposalCount}
      />
      
      {/* Main content */}
      <div className="flex-1 overflow-y-auto" style={{ backgroundColor: '#efede7' }}>
        <div className="p-4 lg:p-6">
          <VStack gap={6}>
            {/* Cvent Venue Matching Results (if connected) */}
            {connectedRfp && venueMatches && (
              <CventVenueMatchingResults
                eventId={eventId}
                matchResults={venueMatches}
                destinations={destinations.map(d => ({
                  id: d.destination.id,
                  city: d.destination.city,
                  country: d.destination.country,
                }))}
                onVenueAdded={loadShortlistOnly}
                onVenueCreated={loadShortlistOnly}
                onViewVenueDetails={handleViewDetails}
              />
            )}

            {/* Loading matches state */}
            {connectedRfp && loadingMatches && (
              <Card>
                <CardContent>
                  <LoadingState text="Loading venue matches..." />
                </CardContent>
              </Card>
            )}

            {/* Shortlisted Venues Table */}
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4">
                Shortlisted Venues
              </h2>
              <RFPVenuesTable
                venuesWithDestination={venuesWithDestination}
                onViewDetails={handleViewDetails}
                onUpdateNotes={handleUpdateNotes}
                cventRfpId={event?.cvent_rfp_id || null}
                eventId={eventId}
                cventVenueMap={cventVenueMap}
                cventSuppliers={cventSuppliersList}
                onVenueDisconnected={() => {
                  // Reload data to refresh connections and retrigger matching
                  loadData();
                }}
                onVenueRemovedFromShortlist={() => {
                  // Reload shortlist to reflect the removal
                  loadData();
                }}
                onManualProposalMarked={() => {
                  // Reload data to refresh the table with updated proposal data
                  loadData();
                }}
                onVenueManuallyConnected={() => {
                  // Reload to pick up the new cvent_supplier_id + status
                  loadData();
                }}
              />
            </div>
          </VStack>
        </div>
      </div>
      
      {/* Venue Details Modal */}
      <VenueDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedVenue(null);
        }}
        venue={selectedVenue}
        isShortlisted={true}
        // No add to shortlist action since we're already in review mode
      />
    </div>
  );
}

